import * as React from 'react';
import type { Channel, UnitPriceType } from 'shared/src/domain/line-item/line-item-channels';
import { hasSkillPlatformConfig } from 'shared/src/domain/ai/skills/skill-platform-registry';
import {
  lineItemConfigOptions,
  type AvailablePlatform
} from 'shared/src/domain/line-item/line-item-config-options';

export type CardSelection = {
  channelId?: number;
  tacticId?: number;
  priceTypeId?: number;
  platformIds: number[];
  /** Budget for this line item. Required to finalize; auto-filled to the remaining budget for a
   * single line item, split by the user when creating several. */
  price?: number;
  /** Per-unit price (stored per unit, so CPM is per impression). Undefined means "use the rate
   * card's price"; null means the user cleared the field, so it stays blank instead of snapping
   * back to the rate card. Set when the user edits it or the rate card has none (e.g. SEM at $0). */
  unitPrice?: number | null;
};

/** The rate-card entry for the selection's tactic + price type, if both are set and valid. */
function selectedRate(selection: CardSelection, channels: Channel[]) {
  if (selection.tacticId === undefined || selection.priceTypeId === undefined) return undefined;
  return channels
    .flatMap(c => c.tactics)
    .find(t => t.id === selection.tacticId)
    ?.rates.find(r => r.unitPriceType.id === selection.priceTypeId);
}

/** Per-unit price the line item will be created with: the user's entry, else the rate card's when
 * it has one. Undefined when neither exists, which blocks finalize. */
export function effectiveUnitPrice(
  selection: CardSelection,
  channels: Channel[]
): number | undefined {
  if (selection.unitPrice === null) return undefined;
  if (selection.unitPrice !== undefined) return selection.unitPrice;
  const rateUnitPrice = selectedRate(selection, channels)?.unitPrice;
  return rateUnitPrice !== undefined && rateUnitPrice > 0 ? rateUnitPrice : undefined;
}

/** Planned per-unit cost: the effective unit price less the rate card's target margin. */
export function effectivePlannedUnitCost(
  selection: CardSelection,
  channels: Channel[]
): number | undefined {
  const unitPrice = effectiveUnitPrice(selection, channels);
  if (unitPrice === undefined) return undefined;
  return round6(unitPrice * (1 - (selectedRate(selection, channels)?.targetMargin ?? 0)));
}

/** Converts a unit price as traders quote it (CPM per thousand impressions) to the per-unit value
 * the card stores, for the selection's price type. */
export function perUnitFromQuoted(quoted: number, selection: CardSelection, channels: Channel[]) {
  return fromDisplayUnitPrice(quoted, selectedRate(selection, channels)?.unitPriceType) ?? quoted;
}

/** A line-item config is complete when the cascade is filled and a positive price and unit price
 * are set.
 * The channel/tactic must exist in the rate card (agent prefills can carry stale/invalid ids).
 * Platforms are optional; any that ARE selected must be valid for the selected tactic. */
export function isSelectionComplete(selection: CardSelection, channels: Channel[]): boolean {
  if (
    selection.channelId === undefined ||
    selection.tacticId === undefined ||
    selection.priceTypeId === undefined ||
    selection.price === undefined ||
    selection.price <= 0
  ) {
    return false;
  }
  const unitPrice = effectiveUnitPrice(selection, channels);
  if (unitPrice === undefined || unitPrice <= 0) return false;
  const tactic = channels
    .find(c => c.id === selection.channelId)
    ?.tactics.find(t => t.id === selection.tacticId);
  if (!tactic) return false;
  const validIds = new Set(tactic.platforms.map(p => p.id));
  return selection.platformIds.every(id => validIds.has(id));
}

export type InferredFromZohoFields = {
  channel?: boolean;
  tactic?: boolean;
  priceType?: boolean;
  platforms?: boolean;
};

type ConfigurationCardProps = {
  channels: Channel[];
  selection: CardSelection;
  remainingBudget: number;
  isBusy: boolean;
  inferredFromZoho?: InferredFromZohoFields;
  onChange: (next: CardSelection) => void;
  /** Repeater chrome: a label like "Line item 2" and a remove handler when more than one is shown. */
  headerLabel?: string;
  onRemove?: () => void;
};

export function ConfigurationCard({
  channels,
  selection,
  remainingBudget,
  isBusy,
  inferredFromZoho,
  onChange,
  headerLabel,
  onRemove
}: ConfigurationCardProps) {
  const options = lineItemConfigOptions(
    {
      channelId: selection.channelId,
      tacticId: selection.tacticId,
      priceTypeId: selection.priceTypeId
    },
    channels,
    hasSkillPlatformConfig
  );

  const status = getConfigStatus(selection, isBusy, effectiveUnitPrice(selection, channels));
  const priceType = selectedRate(selection, channels)?.unitPriceType;
  const unitPriceDisplay = toDisplayUnitPrice(effectiveUnitPrice(selection, channels), priceType);

  function applyChannel(id?: number) {
    if (id === selection.channelId) return;
    // Keep platformIds — no tactic context yet to filter against; the next applyTactic
    // narrows them. This lets a Zoho-pre-filled platform (e.g. Beeswax) survive the
    // user's channel pick instead of getting wiped before the platform field renders.
    onChange({
      channelId: id,
      tacticId: undefined,
      priceTypeId: undefined,
      platformIds: selection.platformIds
    });
  }

  function applyTactic(id?: number) {
    if (id === selection.tacticId) return;
    const channel = channels.find(c => c.id === selection.channelId);
    const tactic = channel?.tactics.find(t => t.id === id);
    const allowed = new Set(tactic?.platforms.map(p => p.id) ?? []);
    const retained = selection.platformIds.filter(pid => allowed.has(pid));
    onChange({
      channelId: selection.channelId,
      tacticId: id,
      priceTypeId: undefined,
      platformIds: retained
    });
  }

  function applyPriceType(id?: number) {
    if (id === selection.priceTypeId) return;
    // A new price type has its own rate-card price, so drop any unit price entered for the old one.
    onChange({ ...selection, priceTypeId: id, unitPrice: undefined });
  }

  function applyPlatforms(nextIds: number[]) {
    onChange({ ...selection, platformIds: nextIds });
  }

  function applyPrice(value?: number) {
    onChange({ ...selection, price: value });
  }

  function applyUnitPrice(displayValue?: number) {
    // An emptied field is null (cleared), not undefined (untouched), so it stays blank.
    onChange({ ...selection, unitPrice: fromDisplayUnitPrice(displayValue, priceType) ?? null });
  }

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm transition-colors ${
        status.type === 'awaiting'
          ? 'border-amber-300 bg-amber-50/30'
          : status.type === 'populating'
            ? 'border-blue-300 bg-blue-50/30'
            : 'border-gray-200 bg-white'
      }`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">{headerLabel ?? 'Configuration'}</h3>
          <StatusBadge status={status} />
        </div>
        {onRemove ? (
          <button type="button" onClick={onRemove} className="text-xs text-red-500 hover:underline">
            Remove
          </button>
        ) : (
          <span className="text-xs text-gray-500">Budget: ${remainingBudget.toLocaleString()}</span>
        )}
      </div>

      <div className="space-y-3">
        <SelectionField
          label="Channel"
          value={selection.channelId}
          options={options.availableChannels}
          onChange={e => applyChannel(numberOrUndefined(e.target.value))}
          placeholder="Select a channel..."
          inferredFromZoho={inferredFromZoho?.channel}
        />

        {selection.channelId !== undefined ? (
          <SelectionField
            label="Tactic"
            value={selection.tacticId}
            options={options.availableTactics}
            onChange={e => applyTactic(numberOrUndefined(e.target.value))}
            placeholder={
              options.availableTactics.length > 0 ? 'Select a tactic...' : 'Select channel first'
            }
            disabled={options.availableTactics.length === 0}
            inferredFromZoho={inferredFromZoho?.tactic}
          />
        ) : null}

        {selection.tacticId !== undefined ? (
          <PlatformsField
            selectedIds={selection.platformIds}
            options={options.availablePlatforms}
            onChange={applyPlatforms}
            inferredFromZoho={inferredFromZoho?.platforms}
          />
        ) : null}

        {selection.tacticId !== undefined ? (
          <SelectionField
            label="Unit Price Type"
            value={selection.priceTypeId}
            options={options.availablePriceTypes}
            onChange={e => applyPriceType(numberOrUndefined(e.target.value))}
            placeholder={
              options.availablePriceTypes.length > 0
                ? 'Select a price type...'
                : 'Select tactic first'
            }
            disabled={options.availablePriceTypes.length === 0}
            inferredFromZoho={inferredFromZoho?.priceType}
          />
        ) : null}

        {selection.priceTypeId !== undefined ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              Unit Price (${priceType ? ` ${priceType.name}` : ''})
            </label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={unitPriceDisplay ?? ''}
              onChange={e => applyUnitPrice(numberOrUndefined(e.target.value))}
              placeholder="e.g. 2.50"
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900"
            />
          </div>
        ) : null}

        {selection.priceTypeId !== undefined ? (
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">Budget ($)</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={selection.price ?? ''}
              onChange={e => applyPrice(numberOrUndefined(e.target.value))}
              placeholder="e.g. 5000"
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

type ConfigStatus =
  | { type: 'populating'; label: string }
  | { type: 'awaiting'; label: string }
  | { type: 'complete'; label: string };

function getConfigStatus(
  selection: CardSelection,
  isBusy: boolean,
  unitPrice: number | undefined
): ConfigStatus {
  if (isBusy) return { type: 'populating', label: 'Working...' };
  if (selection.channelId === undefined) return { type: 'awaiting', label: 'Select a channel' };
  if (selection.tacticId === undefined) return { type: 'awaiting', label: 'Select a tactic' };
  if (selection.priceTypeId === undefined)
    return { type: 'awaiting', label: 'Select a price type' };
  if (unitPrice === undefined || unitPrice <= 0)
    return { type: 'awaiting', label: 'Set a unit price' };
  if (selection.price === undefined || selection.price <= 0)
    return { type: 'awaiting', label: 'Set a budget' };
  return { type: 'complete', label: 'Ready' };
}

function StatusBadge({ status }: { status: ConfigStatus }) {
  if (status.type === 'populating') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
        {status.label}
      </span>
    );
  }
  if (status.type === 'awaiting') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        {status.label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700">
      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
      {status.label}
    </span>
  );
}

function SelectionField({
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  inferredFromZoho
}: {
  label: string;
  value?: number;
  options: Array<{ id: number; name: string }>;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder: string;
  disabled?: boolean;
  inferredFromZoho?: boolean;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <label className="block text-xs font-medium text-gray-500">{label}</label>
        {inferredFromZoho ? <ZohoInferredBadge /> : null}
      </div>
      <select
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled || options.length === 0}
        className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm leading-normal text-gray-900 disabled:bg-gray-50 disabled:text-gray-400">
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map(opt => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function ZohoInferredBadge() {
  return (
    <span
      title="Inferred from Zoho — click to change"
      className="inline-flex items-center rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-purple-700">
      from Zoho
    </span>
  );
}

function PlatformsField({
  selectedIds,
  options,
  onChange,
  inferredFromZoho
}: {
  selectedIds: number[];
  options: AvailablePlatform[];
  onChange: (nextIds: number[]) => void;
  inferredFromZoho?: boolean;
}) {
  function toggle(id: number, checked: boolean) {
    const next = checked ? [...selectedIds, id] : selectedIds.filter(x => x !== id);
    onChange(next);
  }

  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <label className="block text-xs font-medium text-gray-500">Platform(s) (optional)</label>
        {inferredFromZoho ? <ZohoInferredBadge /> : null}
      </div>
      {options.length === 0 ? (
        <div className="rounded-md border border-gray-300 bg-gray-50 px-2 py-2 text-xs text-gray-400">
          Select tactic first
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {options.map(opt => {
            const checked = selectedIds.includes(opt.id);
            return (
              <label
                key={opt.id}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${
                  checked
                    ? 'border-blue-400 bg-blue-50 text-blue-800'
                    : 'border-gray-300 bg-white text-gray-800 hover:border-gray-400'
                }`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => toggle(opt.id, e.target.checked)}
                  className="h-3 w-3"
                />
                <span>{opt.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Unit prices are stored per unit but entered the way traders quote them, so CPM is shown per
// thousand impressions (same convention as the line-item form's adjustForCPM / undoCPM).
function toDisplayUnitPrice(
  perUnit: number | undefined,
  priceType: UnitPriceType | undefined
): number | undefined {
  if (perUnit === undefined) return undefined;
  return priceType?.name === 'CPM' ? round6(perUnit * 1000) : perUnit;
}

function fromDisplayUnitPrice(
  display: number | undefined,
  priceType: UnitPriceType | undefined
): number | undefined {
  if (display === undefined) return undefined;
  // Rounding the quoted figure before dividing gives a clean per-unit value (CPM 8.2 → 0.0082, not
  // 0.008199999999999999) without dropping CPM precision the way rounding the result would.
  return priceType?.name === 'CPM' ? Math.round(display * 1e6) / 1e9 : display;
}

// Drops floating-point tails while keeping sub-cent per-unit precision (e.g. $0.001495 per
// impression for a CPM line item).
function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

function numberOrUndefined(s: string): number | undefined {
  if (s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}
